import React, { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as InfoIcon } from '@pkg/assets/icons/v2/info.svg';
import { Tooltip } from '@pkg/components';
import { MODERATE_EVERYONE } from '@pkg/components/Sections/Groups';
import {
    useAppDispatch,
    useAppSelector,
    useBreakpoints,
    useDeleteGroup,
    useDeleteMember,
    useGetGroup,
    useGetGroupMemberByUserId,
    useGetModerationPermission,
    useIsCurrentUser,
    useIsGroupMember,
    useMembersPanel,
} from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { Groups } from '@app/client';
import { addNotification } from '@app/store/actions/notification';

import { useUpdateCache } from '../../../utilities';

import { SettingsAction, SettingsActionGroup } from './components';
import { OwnerSettings } from './sections';

import { DisabledDeleteText, DisabledDeleteTextLabel, DisabledDeleteWrapper, Section, StyledSettings } from './styles';

interface SettingsProps {
    showUnfollowButton: boolean;
    onClose(): void;
}

export function Settings({ showUnfollowButton, onClose }: SettingsProps) {
    const { t: translate } = useTranslation('groups');
    const { tablet } = useMediaQuery();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const currentUserId = useAppSelector((state) => state.user?.userAccount?.user.id);
    const userMember = useGetGroupMemberByUserId(currentUserId);

    const { data: moderationData } = useGetModerationPermission();
    const canModerateEveryone = moderationData?.moderation_level === MODERATE_EVERYONE;

    const { data: groupData } = useGetGroup();
    const { mutate: deletePod } = useDeleteGroup();
    const { mutate: deleteMember } = useDeleteMember();
    const { isMobile } = useBreakpoints();

    const updateCache = useUpdateCache(groupData?.id);
    const isAdmin = useAppSelector((state) => state.user.userClass?.isAdmin);

    const { mutate: unfollowGroup, isLoading: isLoadingUnfollow } = useMutation(
        async () => Groups.callUnfollowGroup({ groupId: groupData?.id }),
        {
            onSuccess: () => {
                updateCache();
                onClose();
            },
        },
    );

    const isMember = useIsGroupMember();
    const isOwner = useIsCurrentUser(groupData?.owner);

    const { openPanel } = useMembersPanel();

    function handleOpenPanel() {
        const groupId = groupData?.id;

        if (!groupId) return;

        openPanel(groupId);
        onClose();
    }

    function handleDeletePod() {
        if (groupData?.id && isAdmin) {
            deletePod(
                { groupId: groupData.id },
                {
                    onSuccess({ name }) {
                        dispatch(
                            addNotification({
                                message: translate('pods_delete_message', { name }),
                            }),
                        );
                        navigate('/');
                    },
                    onError({ message }) {
                        dispatch(addNotification({ message }));
                    },
                },
            );
        }
    }

    function handleLeavePod() {
        if (groupData?.id && userMember) {
            deleteMember(
                { groupId: groupData.id, memberId: userMember.id },
                {
                    onSuccess() {
                        dispatch(
                            addNotification({
                                message: translate('pods_leave_message'),
                            }),
                        );
                    },
                    onError({ message }) {
                        dispatch(addNotification({ message }));
                    },
                },
            );
        }
    }

    const handleUnfollow = useCallback(() => {
        if (!isLoadingUnfollow) {
            unfollowGroup();
        }
    }, [unfollowGroup, isLoadingUnfollow]);

    // TODO: This should accept a `disabled={!groupId}` prop.
    const membersActionMarkup = (
        <SettingsAction
            fullWidth={!tablet}
            slim={tablet}
            content={translate(
                isOwner || (canModerateEveryone && isMember)
                    ? 'pods_settings_members_manage'
                    : 'pods_settings_members_view',
            )}
            aria-label={translate('pods_settings_members_a11y')}
            onAction={handleOpenPanel}
        />
    );

    const podAction = (
        <SettingsAction
            destructive
            fullWidth={!tablet}
            slim={tablet}
            content={translate(isOwner ? 'pods_settings_pod_delete' : 'pods_settings_pod_leave')}
            aria-label={translate(isOwner ? 'pods_settings_pod_delete_a11y' : 'pods_settings_pod_leave_a11y')}
            onAction={isOwner ? handleDeletePod : handleLeavePod}
        />
    );

    const disabledDelete = (
        <DisabledDeleteWrapper>
            <DisabledDeleteText>{translate('pods_settings_pod_delete')}</DisabledDeleteText>
            {isMobile ? (
                <DisabledDeleteTextLabel>{translate('pods_settings_pod_delete_disabled')}</DisabledDeleteTextLabel>
            ) : (
                <Tooltip trigger={<InfoIcon />} placement="bottom" variant="secondary">
                    {translate('pods_settings_pod_delete_disabled')}
                </Tooltip>
            )}
        </DisabledDeleteWrapper>
    );

    const unfollowAction = (
        <SettingsAction
            destructive
            fullWidth={!tablet}
            slim={tablet}
            content={'Unfollow'}
            aria-label={'pods_settings_pod_unfollow_a11y'}
            onAction={handleUnfollow}
        />
    );

    return (
        <StyledSettings>
            {isOwner ? <OwnerSettings /> : null}

            <Section $divider={isOwner && tablet}>
                <SettingsActionGroup slim={tablet}>
                    {membersActionMarkup}
                    {isMember ? <>{isOwner && !isAdmin ? disabledDelete : podAction}</> : null}
                    {showUnfollowButton ? unfollowAction : null}
                </SettingsActionGroup>
            </Section>
        </StyledSettings>
    );
}
